import ChevronBottom from '@assets/icons/chevron-down.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Easing,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { formatDate } from '@/utils/date';
import { ItemDetail_OrderHistoryFieldsFragment } from '@/types';
import { useItemDisplayOptions } from '@/contexts/new-order-context';

interface OrderHistoryType {
  orderDate: string;
  ordersItems: ItemDetail_OrderHistoryFieldsFragment[];
}

interface Props {
  loading: boolean;
  onToggle?: () => void;
  orderHistoryItems: ItemDetail_OrderHistoryFieldsFragment[];
}

export const ItemDetailsOrderHistory: React.FC<Props> = ({
  onToggle,
  orderHistoryItems,
  loading,
}) => {
  const itemDisplayOptions = useItemDisplayOptions();
  const [styles, theme] = useStyles();

  const [isOrderHistoryOpen, setOrderHistoryOpen] = useState(false);
  const animatedValueRef = useRef(new Animated.Value(0));
  const orderHistoryContentHeightRef = useRef(0);

  const orderHistory = useMemo(() => {
    if (orderHistoryItems.length === 0) {
      return [];
    }

    const orderDateToItemsMap: {
      [date: string]: ItemDetail_OrderHistoryFieldsFragment[];
    } = {};

    return orderHistoryItems.reduce<OrderHistoryType[]>(
      (result, order_item: ItemDetail_OrderHistoryFieldsFragment) => {
        if (order_item.fulfillment_date) {
          const orderDate = formatDate(order_item.fulfillment_date, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          });
          if (!orderDateToItemsMap[orderDate]) {
            orderDateToItemsMap[orderDate] = [];
            result.push({
              orderDate,
              ordersItems: orderDateToItemsMap[orderDate],
            });
          }

          orderDateToItemsMap[orderDate].push(order_item);
        }
        return result;
      },
      [],
    );
  }, [orderHistoryItems]);

  useEffect(() => {
    Animated.timing(animatedValueRef.current, {
      duration: 250,
      easing: Easing.out(Easing.exp),
      toValue: isOrderHistoryOpen ? 1 : 0,
      useNativeDriver: false,
    }).start();
  }, [isOrderHistoryOpen]);

  return (
    <View style={styles.base}>
      <TouchableOpacity
        onPress={() => {
          setOrderHistoryOpen((open) => !open);
          onToggle?.();
        }}
        style={styles.touchable}
      >
        <Text style={styles.title}>{i18n.t('fulfillment_history')}</Text>
        <Animated.View
          style={{
            transform: [
              {
                rotate: animatedValueRef.current.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0deg', '-180deg'],
                }),
              },
            ],
          }}
        >
          <ChevronBottom fill={theme.colors.primary} width={12} />
        </Animated.View>
      </TouchableOpacity>
      <Animated.View
        style={[
          styles.container,
          {
            height: animatedValueRef.current.interpolate({
              inputRange: [0, 1],
              outputRange: [0, orderHistoryContentHeightRef.current],
            }),
          },
        ]}
      >
        {loading ? (
          <ActivityIndicator />
        ) : (
          <View
            onLayout={(e) => {
              orderHistoryContentHeightRef.current =
                e.nativeEvent.layout.height;
            }}
            style={styles.content}
          >
            {orderHistory.map((orderHistoryEntry) => {
              return (
                <View key={orderHistoryEntry.orderDate} style={styles.entry}>
                  <Text style={[TYPOGRAPHY.small, FONTS.primarySemiBold]}>
                    {orderHistoryEntry.orderDate}
                  </Text>
                  {orderHistoryEntry.ordersItems.map((orderItem, index) => {
                    const orderItemText =
                      orderItem.item_modifiers &&
                      itemDisplayOptions.itemModifiersEnabled
                        ? `${orderItem.unit} · ${orderItem.item_modifiers}`
                        : orderItem.unit;
                    return (
                      <View key={index} style={styles.details}>
                        <View style={styles.quantityBox}>
                          <Text style={styles.quantity}>
                            {orderItem.quantity}
                          </Text>
                        </View>
                        <View style={styles.description}>
                          <Text style={TYPOGRAPHY.small}>{orderItemText}</Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              );
            })}
          </View>
        )}
      </Animated.View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
  },
  container: {
    overflow: 'hidden',
  },
  content: {
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  description: {
    alignItems: 'center',
    flexDirection: 'row',
    marginLeft: 16,
  },
  details: {
    flexDirection: 'row',
    marginTop: 8,
  },
  entry: {
    marginBottom: 16,
  },
  quantity: {
    ...FONTS.primaryBold,
    fontSize: theme.fontSizes.small,
  },
  quantityBox: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: 1,
    color: theme.colors.primary,
    fontSize: theme.fontSizes.small,
    height: 24,
    justifyContent: 'center',
    maxWidth: 40,
    minWidth: 24,
  },
  title: {
    ...FONTS.primaryBold,
    color: theme.colors.inactiveText,
    textTransform: 'uppercase',
  },
  touchable: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 14,
    marginTop: 16,
  },
}));
